import { createSelector } from '@ngrx/store';
import { FilteredClientAccount } from '@shared/models';
import { selectMultiInheritanceToggle } from '@store/selectors/feature/feature.selectors';
import { selectEmploymentFeature, VgEmploymentState } from 'vg-interaction-model/dist';

import {
  getBeneficiariesState,
  getDecedentInfoState,
  getFeatureToggleState,
  getTriageState,
} from '../../../reducers';
import { BeneficiariesState } from '../../../reducers/beneficiaries/beneficiaries.reducer';
import { FeatureToggleState } from '../../../reducers/feature/feature.reducer';
import { ISelectedClientAccountsState } from '../../../reducers/selected-client-accounts/selected-client-accounts.reducer';
import { TriageState } from '../../../reducers/triage/triage.reducer';
import { selectEligibleIndividualAccounts } from '../../client-accounts/client-accounts.selectors';
import { selectedClientAccountsState } from '../../selected-client-accounts/selected-client-accounts.selectors';
import {
  selectIsClient,
  selectIsIndividualDesignationExecutor,
  selectIsIRASingleExecutor,
  selectisIRAToEstateFlow,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
} from '../../triage/triage.selectors';
import { nullState } from '../common/router.selectors';
import {
  getInheritanceAssumeNextPage,
  getInheritanceBeneDetailsNextPage,
  getInheritanceBeneRelationshipNextPage,
  getInheritanceContactInfoNextPage,
  getInheritanceDecedentInfoRoute,
  getInheritanceEmploymentNextPage,
  getInheritanceEstateNextPage,
  getInheritanceIncomeNetWorthNextPage,
  getInheritanceInvestObjNextPage,
  getInheritanceIraTypeNextPage,
  getInheritanceLetterUploadNexPage,
  getInheritanceOverviewNextPage,
  getInheritancePersonalInfoNextPage,
  getInheritanceReviewNextPage,
  getInheritanceTCFTrustInfoNextPage,
  getInheritanceTCFTrustSignNextPage,
  getInheritanceTermsNextPage,
  getInheritanceTransferFromNextPage,
  getInheritanceTransferToNextPage,
  getInheritanceTriageNextPage,
  getInheritanceTrustInfoNextPage,
  getInheritanceTrustTaxInfoNextPage,
  getInheritanceUploadNextPage,
} from './inheritance-router.helpers';

export const selectInheritanceOverviewNextPage = createSelector(
  selectMultiInheritanceToggle,
  (multiInheritanceToggleState: boolean) =>
    getInheritanceOverviewNextPage(multiInheritanceToggleState),
);

export const selectInheritanceTriageNextPage = createSelector(
  getTriageState,
  getFeatureToggleState,
  (triageState: TriageState, featureToggleState: FeatureToggleState) =>
    getInheritanceTriageNextPage(triageState, featureToggleState),
);
export const selectInheritanceDecedentInfoNextPage = createSelector(
  getDecedentInfoState,
  getTriageState,
  getFeatureToggleState,
  selectEligibleIndividualAccounts(),
  selectIsIndividualDesignationExecutor,
  selectisIRAToEstateFlow,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
  (
    decedentInfoState,
    triageState,
    featureToggleState,
    eligibleAccounts,
    indvDesignationExecutor,
    isIRAToEstateFlow,
    isTrustFlow,
    isTrustToTrustFlow,
  ) =>
    getInheritanceDecedentInfoRoute(
      decedentInfoState,
      triageState,
      featureToggleState,
      eligibleAccounts,
      indvDesignationExecutor,
      isIRAToEstateFlow,
      isTrustFlow,
      isTrustToTrustFlow,
    ),
);

export const selectInheritanceUploadNextPage = createSelector(
  getTriageState,
  getFeatureToggleState,
  selectEligibleIndividualAccounts(),
  selectIsIndividualDesignationExecutor,
  selectIsTrustToTrustFlow,
  (
    triageState: TriageState,
    featureToggleState: FeatureToggleState,
    eligibleAccounts: FilteredClientAccount,
    indvDesignationExecutor: boolean,
    isTrustToTrustFlow: boolean,
  ) =>
    getInheritanceUploadNextPage(
      triageState,
      featureToggleState,
      eligibleAccounts,
      indvDesignationExecutor,
      isTrustToTrustFlow,
    ),
);
export const selectInheritanceIraTypeNextPage = createSelector(
  getTriageState,
  selectedClientAccountsState,
  (triageState: TriageState, selectedAccounts: ISelectedClientAccountsState) =>
    getInheritanceIraTypeNextPage(triageState, selectedAccounts),
);

export const selectInheritanceBeneRelationshipNextPage = createSelector(
  getBeneficiariesState,
  (beneficiariesState: BeneficiariesState) =>
    getInheritanceBeneRelationshipNextPage(beneficiariesState),
);
export const selectInheritanceTransferFromNextPage = createSelector(
  selectEligibleIndividualAccounts(),
  (individualAccounts: FilteredClientAccount) =>
    getInheritanceTransferFromNextPage(individualAccounts),
);

export const selectInheritanceTransferToNextPage = createSelector(
  selectedClientAccountsState,
  (selectedAccounts: ISelectedClientAccountsState) =>
    getInheritanceTransferToNextPage(selectedAccounts),
);

export const selectInheritanceAssumeNextPage = createSelector(
  getTriageState,
  selectedClientAccountsState,
  (triageState: TriageState, selectedAccounts: ISelectedClientAccountsState) =>
    getInheritanceAssumeNextPage(triageState, selectedAccounts),
);
export const selectInheritanceBeneDetailsNextPage = createSelector(nullState, () =>
  getInheritanceBeneDetailsNextPage(),
);
export const selectInheritancePersonalInfoNextPage = createSelector(nullState, () =>
  getInheritancePersonalInfoNextPage(),
);
export const selectInheritanceContactInfoNextPage = createSelector(
  selectIsIRASingleExecutor,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
  selectIsClient,
  (
    isIRASingleExecutor: boolean,
    isTrustee: boolean,
    isTrustToTrustFlow: boolean,
    isClient: boolean,
  ) =>
    getInheritanceContactInfoNextPage(isIRASingleExecutor, isTrustee, isTrustToTrustFlow, isClient),
);
export const selectInheritanceEmploymentNextPage = createSelector(
  getTriageState,
  selectEmploymentFeature,
  (triageState: TriageState, employmentState: VgEmploymentState) =>
    getInheritanceEmploymentNextPage(triageState, employmentState),
);
export const selectInheritanceLetterUploadNextPage = createSelector(nullState, () =>
  getInheritanceLetterUploadNexPage(),
);
export const selectInheritanceReviewNextPage = createSelector(
  selectedClientAccountsState,
  (selectedAccounts: ISelectedClientAccountsState) =>
    getInheritanceReviewNextPage(selectedAccounts),
);

export const selectInheritanceInvestObjNextPage = createSelector(nullState, () =>
  getInheritanceInvestObjNextPage(),
);
export const selectInheritanceIncomeNetWorthNextPage = createSelector(nullState, () =>
  getInheritanceIncomeNetWorthNextPage(),
);
export const selectInheritanceEstateNextPage = createSelector(
  getTriageState,
  (triageState: TriageState) => getInheritanceEstateNextPage(triageState),
);

export const selectInheritanceTermsNextPage = createSelector(nullState, () =>
  getInheritanceTermsNextPage(),
);

export const selectInheritanceTrustInfoNextPage = createSelector(nullState, () =>
  getInheritanceTrustInfoNextPage(),
);

export const selectInheritanceTrustTaxInfoNextPage = createSelector(nullState, () =>
  getInheritanceTrustTaxInfoNextPage(),
);

export const selectInheritanceTCFTrustInfoNextPage = createSelector(nullState, () =>
  getInheritanceTCFTrustInfoNextPage(),
);

export const selectInheritanceTCFTrustSignNextPage = createSelector(nullState, () =>
  getInheritanceTCFTrustSignNextPage(),
);
